// src/hooks/useKbDocuments.ts
//
// React data hook for the Knowledge Base screen. Plain
// useState/useCallback/useEffect per the repo's hook style (no React Query);
// all access goes through kbDocumentsService. Mutations refetch rather than
// update optimistically — the list re-renders only from the server's answer.
// No realtime subscription (recorded decision, mirrors 1.1/1.2): refetch
// suffices for a low-concurrency editor.

import { useState, useEffect, useCallback } from 'react'
import { kbDocumentsService } from '../services/kbDocumentsService'
import type {
  KbMetadataEdit,
  KbUploadInput,
} from '../services/kbDocumentsService'
import type { YakKbDocument, YakKbDocVersion } from '../types/yak'

interface UseKbDocumentsReturn {
  documents: YakKbDocument[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  upload: (file: File, input: KbUploadInput) => Promise<void>
  updateMetadata: (id: string, edit: KbMetadataEdit) => Promise<void>
}

export function useKbDocuments(): UseKbDocumentsReturn {
  const [documents, setDocuments] = useState<YakKbDocument[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchDocuments = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true)
      setError(null)
      setDocuments(await kbDocumentsService.listDocuments())
    } catch (err) {
      // A silent refresh runs after a mutation that already succeeded on the
      // server. Surfacing its failure as the page-level banner would
      // contradict that success; only the initial/explicit load owns the
      // banner. A failed background refresh keeps the last-known list.
      if (!silent) {
        setError(
          err instanceof Error ? err.message : 'Failed to load documents'
        )
      } else {
        console.error('KB document list refresh failed', err)
      }
    } finally {
      if (!silent) setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchDocuments()
  }, [fetchDocuments])

  const upload = useCallback(
    async (file: File, input: KbUploadInput) => {
      await kbDocumentsService.uploadDocument(file, input)
      await fetchDocuments(true)
    },
    [fetchDocuments]
  )

  const updateMetadata = useCallback(
    async (id: string, edit: KbMetadataEdit) => {
      await kbDocumentsService.updateMetadata(id, edit)
      await fetchDocuments(true)
    },
    [fetchDocuments]
  )

  return {
    documents,
    loading,
    error,
    refetch: () => fetchDocuments(),
    upload,
    updateMetadata,
  }
}

interface UseKbDocumentReturn {
  document: YakKbDocument | null
  latestVersion: YakKbDocVersion | null
  versions: YakKbDocVersion[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  updateMetadata: (edit: KbMetadataEdit) => Promise<void>
  replaceContent: (file: File) => Promise<void>
  retire: () => Promise<void>
}

// Single-document hook for the routed detail (/kb/:documentId). Loads the
// document, its latest version (content preview), and the full version history
// (newest-first). The metadata edit is a direct-under-RLS UPDATE that never
// creates a version; replaceContent delegates to the kb-ingest pipeline, which
// publishes a new immutable version atomically. Same silent-refresh rule as the
// list hook — a mutation succeeds server-side, then we refetch quietly.
export function useKbDocument(documentId: string): UseKbDocumentReturn {
  const [document, setDocument] = useState<YakKbDocument | null>(null)
  const [latestVersion, setLatestVersion] = useState<YakKbDocVersion | null>(
    null
  )
  const [versions, setVersions] = useState<YakKbDocVersion[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchDocument = useCallback(
    async (silent = false) => {
      try {
        if (!silent) setLoading(true)
        setError(null)
        const [doc, versionList] = await Promise.all([
          kbDocumentsService.getDocument(documentId),
          kbDocumentsService.listVersions(documentId),
        ])
        setDocument(doc)
        // Newest-first list — the first row is the latest version.
        setVersions(versionList)
        setLatestVersion(versionList[0] ?? null)
      } catch (err) {
        if (!silent) {
          setError(
            err instanceof Error ? err.message : 'Failed to load the document'
          )
        } else {
          console.error('KB document refresh failed', err)
        }
      } finally {
        if (!silent) setLoading(false)
      }
    },
    [documentId]
  )

  useEffect(() => {
    fetchDocument()
  }, [fetchDocument])

  const updateMetadata = useCallback(
    async (edit: KbMetadataEdit) => {
      await kbDocumentsService.updateMetadata(documentId, edit)
      await fetchDocument(true)
    },
    [documentId, fetchDocument]
  )

  const replaceContent = useCallback(
    async (file: File) => {
      await kbDocumentsService.replaceContent(documentId, file)
      await fetchDocument(true)
    },
    [documentId, fetchDocument]
  )

  // Retire is terminal (recorded decision 5): the service flips status once and
  // we refetch quietly so the detail re-renders read-only from the server's
  // answer — no optimistic lifecycle change. The failed-silent-refetch rule
  // applies (a background refresh failure never contradicts a successful retire).
  const retire = useCallback(async () => {
    await kbDocumentsService.retireDocument(documentId)
    await fetchDocument(true)
  }, [documentId, fetchDocument])

  return {
    document,
    latestVersion,
    versions,
    loading,
    error,
    refetch: () => fetchDocument(),
    updateMetadata,
    replaceContent,
    retire,
  }
}

interface UseKbVersionReturn {
  version: YakKbDocVersion | null
  content: string
  loading: boolean
  error: string | null
}

// Read-only single-version hook for the deep-linkable version view
// (/kb/:documentId/versions/:versionId). Loads the version row + its content
// (content_text, or an ordered chunk-concatenation fallback). This is the
// surface Epic 4's ticket screen deep-links to for pinned sources, so it must
// resolve any historical version — not just the latest.
export function useKbVersion(versionId: string): UseKbVersionReturn {
  const [version, setVersion] = useState<YakKbDocVersion | null>(null)
  const [content, setContent] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchVersion = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const result = await kbDocumentsService.getVersionContent(versionId)
      setVersion(result?.version ?? null)
      setContent(result?.content ?? '')
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load the version'
      )
    } finally {
      setLoading(false)
    }
  }, [versionId])

  useEffect(() => {
    fetchVersion()
  }, [fetchVersion])

  return { version, content, loading, error }
}
